import { ReactNode } from "react";
import { Card } from "@/components/ui/card";
import SkillToggle from "./SkillToggle";

interface SkillCardProps {
  icon: ReactNode;
  title: string;
  subtitle: string;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  configSection?: ReactNode;
  disabled?: boolean;
  vertical?: boolean;
}

const SkillCard = ({
  icon,
  title,
  subtitle,
  enabled,
  onToggle,
  configSection,
  disabled = false,
  vertical = false
}: SkillCardProps) => {
  if (vertical) {
    return (
      <Card className="h-full border p-6 flex flex-col">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-3 bg-amber-100 rounded-md text-amber-500">
            {icon}
          </div>
          <h3 className="text-sm font-medium">{title}</h3>
        </div>
        
        <p className="text-sm text-gray-500 flex-grow">{subtitle}</p>
        
        <div className="mt-auto pt-3">
          {/* Configuration section if enabled */}
          {enabled && configSection && (
            <div className="border-t border-gray-100 w-full pb-3">
              {configSection}
            </div>
          )}
          
          {/* Toggle always at the bottom */}
          <div className="flex justify-end pt-2">
            <SkillToggle
              enabled={enabled}
              onToggle={onToggle}
              disabled={disabled}
            />
          </div>
        </div>
      </Card>
    );
  }

  // Original horizontal layout, but with toggle at the bottom
  return (
    <Card className="mb-4 border p-6 flex flex-col h-full">
      <div className="flex items-start">
        {/* Icon section */}
        <div className="p-3 bg-amber-100 rounded-md text-amber-500 mr-4 mt-1">
          {icon}
        </div>
        
        {/* Content section */}
        <div className="flex-1">
          <h3 className="text-sm font-medium mb-2">{title}</h3>
          <p className="text-sm text-gray-500 mb-4">{subtitle}</p>
        </div>
      </div>
      
      <div className="mt-auto">
        {/* Conditional configuration section */}
        {enabled && configSection && (
          <div className="mt-4 pt-4 border-t border-gray-100 mb-3">
            {configSection}
          </div>
        )}
        
        {/* Toggle at the bottom */}
        <div className="flex justify-end">
          <SkillToggle
            enabled={enabled}
            onToggle={onToggle}
            disabled={disabled}
          />
        </div>
      </div>
    </Card>
  );
};

export default SkillCard; 